import {
  filter,
  append,
  any,
  allPass,
  concat,
  reduce,
  map,
  propEq,
  match,
  pipe,
  nth,
  path,
  init,
  last,
} from 'ramda';

import {
  applyAssociationPropertiesAttr,
  applyAssociationPropertiesAssoc,
} from './AssociationPropertiesUtils';

import { Maybe, Result } from 'true-myth';

import type {
  IClientBusinessTypeModel,
  IClientBusinessModel,
  IClientAttributeModel,
  IClientAssociationModel,
  ICollectionInfo,
  ClientPropertiesType,
} from '../Types/ClientModelTypes';

import { isValidForType } from '../Components/Urval/Components/Condition/Components/TypeTreePicker/Utils/typeConditionUtils';
import { ITypeTreeConditionObject } from '../Types/UrvalTypes';
import { AvenyCollectionMap } from './ModelContext/ModelContext';
import Logger, { namedError } from '../Utils/Logger';
import {
  getId,
  getVersion,
  attributeDefToDataPath,
  getValue,
} from '../Utils/dataUtils';
import { INodeData } from '../Components/QueryComponents/NodeQuery/NodeQuery';
import { ValueType } from '../Utils/Formatters/FormatterTypes';

export type AttributePredicateType = (
  attribute: IClientAttributeModel,
  associations?: IClientAssociationModel[]
) => boolean;

export type AssociationPredicateType = (
  association: IClientAssociationModel,
  associations?: IClientAssociationModel[]
) => boolean;

export const UNKNOWN_ATTRIBUTE: IClientAttributeModel = {
  AttributeName: 'UNKNOWN',
  DisplayLabel: 'UNKNOWN',
  Hint: '',
  DataType: 'String',
  EnumValues: new Map([]),
  Attribute: 'Normal',
  Condition: 'Normal',
  Formatter: '',
  FormatterParseParams: '',
  GUIEditable: false,
  FormatterDisplayParams: '',
  FormatterValidateParams: '',
  InterfaceType: '',
  CondOperators: [],
  DefaultCondOperator: undefined,
  ValidInComps: [],
  AttributeGroup: '',
  AttributeParams: {},
  Properties: new Map([]),
  Required: true,
};

export const UNKNOWN_ASSOCIATION: IClientAssociationModel = {
  AssociationName: 'UNKNOWN',
  DisplayLabel: 'UNKNOWN',
  Attribute: 'Normal',
  Condition: 'Normal',
  AssociatedCollectionName: 'UNKNOWN',
  AssociatedComponentName: 'UNKNOWN',
  JoinAssociation: false,
  ValidInComps: [],
  OwnedAssociation: false,
  Integrated: false,
  Properties: new Map([]),
  Required: true,
};

export const UNKNOWN_TYPETREE: IClientBusinessTypeModel = {
  TypeValue: 'UNKNOWN',
  Abstract: false,
  SOLabel: 'UNKNOWN',
  SOGLabel: 'UNKNOWN',
  SBLabel: 'UNKNOWN',
  SBGLabel: 'UNKNOWN',
  POLabel: 'UNKNOWN',
  POGLabel: 'UNKNOWN',
  PBLabel: 'UNKNOWN',
  PBGLabel: 'UNKNOWN',
  Visible: true,
  ChildTypes: [],
};

export const collectionTypeNameToFieldName: (p: string) => Maybe<string> = pipe(
  match(/TED(.*)/),
  nth(1),
  Maybe.of
);

export const getAttributes = (
  businessModel: IClientBusinessModel
): IClientAttributeModel[] => Object.values(businessModel.Attributes);

export const getCollectionFlatAttribute = (
  collectionName: string,
  componentName: string,
  attributePath: string,
  collectionMap: AvenyCollectionMap
) => {
  const split = attributePath.split('.');
  if (split.length === 1) {
    return getAttribute(
      collectionName,
      componentName,
      attributePath,
      collectionMap
    );
  }
  return getAssociatedAttribute(collectionName, split, collectionMap);
};

export const getAssociations = (
  businessModel: IClientBusinessModel
): IClientAssociationModel[] => Object.values(businessModel.Associations);

const getChildTypeValues =
  (values: any) => (typeNode: IClientBusinessTypeModel) => {
    values.push(typeNode.TypeValue);
    typeNode.ChildTypes.forEach(getChildTypeValues(values));
  };

export const getAllChildTypeValues = (
  businessTypeNodes: IClientBusinessTypeModel[]
) => {
  let typeValues: string[] = [];

  businessTypeNodes.forEach(businessTypeNode => {
    getChildTypeValues(typeValues)(businessTypeNode);
  });

  return typeValues;
};

export const getValidClassesList = (
  nodes: IClientBusinessTypeModel[],
  componentName: string,
  path: string[] = []
): string[] => {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.TypeValue.toLowerCase() === componentName.toLowerCase()) {
      getChildTypeValues(path)(node);
      return path;
    } else {
      const compPath = getValidClassesList(
        node.ChildTypes,
        componentName,
        append(node.TypeValue, path)
      );
      if (compPath.length > 0) {
        return compPath;
      }
    }
  }

  return [];
};

export const isValidInClass = (
  collection: IClientBusinessModel,
  componentName?: string | null
): (<T extends IClientAttributeModel | IClientAssociationModel>(
  field: T
) => boolean) => {
  if (componentName == null) {
    return () => true;
  }

  const validClassesList = getValidClassesList(
    [collection.RootType],
    componentName
  );

  return (field: IClientAttributeModel | IClientAssociationModel): boolean => {
    return (
      (field.ValidInComps?.length ?? 0) === 0 ||
      any(
        validInComp => validClassesList.includes(validInComp),
        field.ValidInComps ?? []
      )
    );
  };
};

const integratedAttributesReducer =
  (
    attributesPredicate: AttributePredicateType,
    modelPath: IClientAssociationModel[] | undefined,
    model: AvenyCollectionMap
  ) =>
  (
    integratedAttributes: IAttributeInt[],
    association: IClientAssociationModel
  ): IAttributeInt[] => {
    const collectionInfo = getCollectionInfo(
      association.AssociatedCollectionName,
      association.AssociatedComponentName,
      model
    );

    const pred = allAttributePass([
      attributesPredicate,
      isValidInClass(collectionInfo.collection, collectionInfo.componentName),
    ]);

    let attributes: IAttributeInt[] = filter<IClientAttributeModel>(
      attr => pred(attr, modelPath),
      Object.keys(collectionInfo.collection.Attributes).map(
        key => collectionInfo.collection.Attributes[key]
      )
    ).map(attr => ({ attribute: attr }));

    const attributesAfterPropertiesApplied = applyAssociationPropertiesAttr(
      attributes.map(attr => attr.attribute),
      association,
      model
    );

    attributes = map(
      (attr: IClientAttributeModel): IAttributeInt => ({
        integratedIn: [association],
        attribute: attr,
      }),
      attributesAfterPropertiesApplied
    );

    return attributes.concat(integratedAttributes);
  };

const isIntegrated = propEq('Integrated', true);

const getIntegratedAttributes = (
  attributesPredicate: AttributePredicateType,
  collectionInfo: ICollectionInfo,
  modelPath: IClientAssociationModel[] | undefined,
  model: AvenyCollectionMap
): IAttributeInt[] => {
  const integratedAssociations: IClientAssociationModel[] = filter(
    allPass([
      isIntegrated,
      isValidInClass(collectionInfo.collection, collectionInfo.componentName),
    ]),
    getAssociations(collectionInfo.collection)
  );

  const integratedAttributes = reduce(
    integratedAttributesReducer(attributesPredicate, modelPath, model),
    [],
    integratedAssociations
  );

  return integratedAttributes;
};

export const getCollectionInfo = (
  collectionName: string,
  componentName: string | null | undefined,
  model: AvenyCollectionMap
): ICollectionInfo => {
  const collection = model.get(collectionName.toLowerCase());

  if (!collection) {
    throw new Error(`Could not find collection with name: ${collectionName}`);
  }

  return { collection, collectionName, componentName };
};

export const getAttributesWithIntegrated = (
  predicate: AttributePredicateType,
  collectionName: string,
  componentName: string | null | undefined,
  modelPath: IClientAssociationModel[] | undefined,
  model: AvenyCollectionMap
): IAttributeInt[] => {
  // Find the collectionName and collection from className, save className for valid in classes check
  const collectionInfo = getCollectionInfo(
    collectionName,
    componentName,
    model
  );

  const predicateWithIsValidInClass = allAttributePass([
    predicate,
    isValidInClass(collectionInfo.collection, collectionInfo.componentName),
  ]);

  // Find all non-integrated attributes for the collection (filter VIC)
  const directAttributes: IAttributeInt[] = filter<IClientAttributeModel>(
    attr => predicateWithIsValidInClass(attr, modelPath), // TODO: associations
    Object.keys(collectionInfo.collection.Attributes).map(
      key => collectionInfo.collection.Attributes[key]
    )
  ).map(attr => ({ attribute: attr }));

  // Find all integrated attributes via associations (filter VIC)
  const integratedAttributes: IAttributeInt[] = getIntegratedAttributes(
    predicate,
    collectionInfo,
    modelPath,
    model
  );

  const attributes = concat(directAttributes, integratedAttributes);

  return attributes;
};

////////////////////////////////////////////////////
// Associations with integrated
////////////////////////////////////////////////////

const integratedAssociationsReducer =
  (
    predicate: AssociationPredicateType,
    modelPath: IClientAssociationModel[] | undefined,
    model: AvenyCollectionMap
  ) =>
  (
    integratedAssociations: IAssociationInt[],
    association: IClientAssociationModel
  ): IAssociationInt[] => {
    const collectionInfo = getCollectionInfo(
      association.AssociatedCollectionName,
      association.AssociatedComponentName,
      model
    );

    const validAssociations: IClientAssociationModel[] = filter(
      assoc => predicate(assoc, modelPath),
      getAssociations(collectionInfo.collection)
    );

    let associations: IAssociationInt[] = filter(
      allPass([propEq('Integrated', false)]),
      validAssociations
    ).map(assoc => ({ association: assoc }));

    const associationsAfterPropertiesApplied = applyAssociationPropertiesAssoc(
      associations.map(assoc => assoc.association),
      association
    );

    associations = map(
      assoc => ({ integratedIn: [association], association: assoc }),
      associationsAfterPropertiesApplied
    );

    return concat(integratedAssociations, associations);
  };

const getIntegratedAssociations = (
  predicate: AssociationPredicateType,
  validAssociations: IClientAssociationModel[],
  modelPath: IClientAssociationModel[] | undefined,
  model: AvenyCollectionMap
): IAssociationInt[] => {
  const integratedAssociations = filter(isIntegrated, validAssociations);

  const associations = reduce(
    integratedAssociationsReducer(predicate, modelPath, model),
    [],
    integratedAssociations
  );

  return associations;
};

export const getAssociationsWithIntegrated = (
  predicate: AssociationPredicateType,
  collectionName: string,
  componentName: string | null | undefined,
  modelPath: IClientAssociationModel[] | undefined,
  model: AvenyCollectionMap
): IAssociationInt[] => {
  // Find the collectionName and collection from className, save className for valid in classes check
  const collectionInfo = getCollectionInfo(
    collectionName,
    componentName,
    model
  );

  const pred = allAssociationPass([
    predicate,
    isValidInClass(collectionInfo.collection, collectionInfo.componentName),
  ]);

  const validAssociations: IClientAssociationModel[] = filter(
    assoc => pred(assoc, modelPath),
    getAssociations(collectionInfo.collection)
  );

  // Find all none integrated associations (filter VIC)
  const directAssociations: IAssociationInt[] = filter(
    allPass([propEq('Integrated', false)]),
    validAssociations
  ).map(assoc => ({ association: assoc }));

  // Find all integrated associations via associations (filter VIC)
  const integratedAssociations: IAssociationInt[] = getIntegratedAssociations(
    pred,
    validAssociations,
    modelPath,
    model
  );

  return concat(integratedAssociations, directAssociations);
};

export const getAttribute = (
  collectionName: string,
  componentName: string,
  attributeName: string,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel | undefined => {
  const collectionInfo = getCollectionInfo(
    collectionName,
    componentName,
    collectionMap
  );
  return collectionInfo.collection.Attributes[attributeName];
};

export const getAssociation = (
  collectionName: string,
  componentName: string,
  associationName: string,
  collectionMap: AvenyCollectionMap
): IClientAssociationModel | undefined => {
  const collectionInfo = getCollectionInfo(
    collectionName,
    componentName,
    collectionMap
  );
  return collectionInfo.collection.Associations[associationName];
};

const findBCOMPTreeByComponentName = (
  componentName: string,
  typeNodes: IClientBusinessTypeModel[]
): IClientBusinessTypeModel | null => {
  for (let i = 0; i < typeNodes.length; i++) {
    const node = typeNodes[i];
    if (node.TypeValue.toLowerCase() === componentName.toLowerCase()) {
      // Found type
      return node;
    } else {
      // Keep searching
      const result = findBCOMPTreeByComponentName(
        componentName,
        node.ChildTypes
      );
      if (result !== null) {
        return result;
      }
    }
  }
  return null;
};

/**
 * Gets the component tree structure from className
 */
export const getTypeTree = (
  collectionMap: AvenyCollectionMap,
  collectionName: string,
  componentName?: string | null
): IClientBusinessTypeModel => {
  const collectionInfo = getCollectionInfo(collectionName, '', collectionMap);

  if (!componentName) {
    return collectionInfo.collection.RootType;
  } else {
    let typeTree: IClientBusinessTypeModel | null =
      findBCOMPTreeByComponentName(componentName, [
        collectionInfo.collection.RootType,
      ]);

    if (typeTree == null) {
      Logger.error(`Could not find type tree for component ${componentName}`);
      typeTree = UNKNOWN_TYPETREE;
    }

    return typeTree;
  }
};

export const getAssociatedCollection = (
  collectionName: string,
  attributePath: string[],
  collectionMap: AvenyCollectionMap
): IClientBusinessModel => {
  const collectionInfo = getCollectionInfo(collectionName, '', collectionMap);

  let collection = collectionInfo.collection;
  for (let i = 0; i < attributePath.length; i++) {
    const pathString = attributePath[i];

    const nextAssociation = collection.Associations[pathString];

    if (nextAssociation != null) {
      collection = getCollectionInfo(
        nextAssociation.AssociatedCollectionName,
        '',
        collectionMap
      ).collection;
    } else {
      Logger.error(`Could not get associated collection ${pathString}`);
    }
  }
  return collection;
};

export const getAssociatedAttribute = (
  collectionName: string,
  attributePath: string[],
  collectionMap: AvenyCollectionMap
): IClientAttributeModel => {
  const collection = getAssociatedCollection(
    collectionName,
    attributePath.slice(0, attributePath.length - 1),
    collectionMap
  );

  let attribute =
    collection.Attributes[attributePath[attributePath.length - 1]];

  if (attribute == null) {
    Logger.error(
      namedError(
        'avenyModelUtilsCouldNotFindAttribute',
        'Could not find attribute with attributeName: ' +
          attributePath[attributePath.length - 1]
      )
    );

    attribute = UNKNOWN_ATTRIBUTE;
  }

  return attribute;
};

const isCondition = propEq('Condition', 'Normal');
const isNormalAttribute = propEq('Attribute', 'Normal');
const isJoinAssociation = propEq('JoinAssociation', true);

export interface IAttributeInt {
  integratedIn?: IClientAssociationModel[];
  attribute: IClientAttributeModel;
}

export interface IAssociationInt {
  integratedIn?: IClientAssociationModel[];
  association: IClientAssociationModel;
}

const allAttributePass = (
  preds: AttributePredicateType[]
): AttributePredicateType => {
  return (
    attribute: IClientAttributeModel,
    associations?: IClientAssociationModel[]
  ): boolean => {
    for (let i = 0; i < preds.length; i++) {
      const pred = preds[i];
      const res = pred(attribute, associations);
      if (!res) {
        return false;
      }
    }

    return true;
  };
};

const allAssociationPass = (
  preds: AssociationPredicateType[]
): AssociationPredicateType => {
  return (
    association: IClientAssociationModel,
    associations?: IClientAssociationModel[]
  ): boolean => {
    for (let i = 0; i < preds.length; i++) {
      const pred = preds[i];
      const res = pred(association, associations);
      if (!res) {
        return false;
      }
    }

    return true;
  };
};

interface IModelConditionFields {
  attributesWithoutGroup: IAttributeInt[];
  attributesWithGroup: IAttributeInt[];
  pageAssociations: IAssociationInt[];
  extendedAssociations: IAssociationInt[];
}
export const getConditionFields = (
  collectionName: string,
  componentName: string | null | undefined,
  attributePredicate: AttributePredicateType,
  associationPredicate: AssociationPredicateType,
  modelPath: IClientAssociationModel[] | undefined,
  model: AvenyCollectionMap,
  typeConditionObject?: ITypeTreeConditionObject
): IModelConditionFields => {
  const attrPredicate = allAttributePass([isCondition, attributePredicate]);
  const assocPredicate = allAssociationPass([
    isCondition,
    associationPredicate,
  ]);

  const attributes = getAttributesWithIntegrated(
    attrPredicate,
    collectionName,
    componentName,
    modelPath,
    model
  );
  const associations = getAssociationsWithIntegrated(
    assocPredicate,
    collectionName,
    componentName,
    modelPath,
    model
  );

  const attrIsValidForType = (attr: IAttributeInt) =>
    isValidForType(
      attr.integratedIn
        ? attr.integratedIn[0].ValidInComps
        : attr.attribute.ValidInComps ?? [],
      typeConditionObject
    );

  const assocIsValidForType = (assoc: IAssociationInt) =>
    isValidForType(
      assoc.integratedIn
        ? assoc.integratedIn[0].ValidInComps
        : assoc.association.ValidInComps,
      typeConditionObject
    );

  const attributesWithoutGroup = attributes.filter(
    attribute =>
      !attribute.attribute.AttributeGroup && attrIsValidForType(attribute)
  );
  const attributesWithGroup = attributes.filter(
    attribute =>
      attribute.attribute.AttributeGroup && attrIsValidForType(attribute)
  );

  const pageAssociations = associations.filter(
    association =>
      association.association.JoinAssociation &&
      assocIsValidForType(association)
  );
  const extendedAssociations = associations.filter(
    association =>
      !association.association.JoinAssociation &&
      assocIsValidForType(association)
  );

  return {
    attributesWithoutGroup,
    attributesWithGroup,
    pageAssociations,
    extendedAssociations,
  };
};

interface IModelColumnFields {
  attributeIntsWithoutGroup: IAttributeInt[];
  attributeIntsWithGroup: IAttributeInt[];
  associationInts: IAssociationInt[];
}
export const getColumnFields = (
  collectionName: string,
  componentName: string | null | undefined,
  collectionMap: AvenyCollectionMap
): IModelColumnFields => {
  const attributes = getAttributesWithIntegrated(
    isNormalAttribute,
    collectionName,
    componentName,
    undefined,
    collectionMap
  );
  const associationInts = getAssociationsWithIntegrated(
    allPass([isNormalAttribute, isJoinAssociation]),
    collectionName,
    componentName,
    undefined,
    collectionMap
  );

  const attributeIntsWithoutGroup = attributes.filter(
    attribute => !attribute.attribute.AttributeGroup
  );
  const attributeIntsWithGroup = attributes.filter(
    attribute => attribute.attribute.AttributeGroup
  );

  return { attributeIntsWithoutGroup, attributeIntsWithGroup, associationInts };
};

export interface IAttributeInfo {
  attribute: IClientAttributeModel;
  viewPath: string;
  collectionRootType: IClientBusinessTypeModel;
  collection: IClientBusinessModel;
}
/**
 * Returns an attribute from a collection with the path modelpath, also returns the
 * path it should have in the userinterface (viewPath) and rootType for the collection the attribute exists in
 * viewPath takes into acount integrated associations
 * @param maybeCollection either collectionName or the collection
 * @param modelPath dot delimited path to the attribute
 */
export const getAttributeInfo = (
  maybeCollection: string | IClientBusinessModel,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): IAttributeInfo => {
  let collection =
    typeof maybeCollection === 'string'
      ? getCollectionInfo(maybeCollection, null, collectionMap).collection
      : maybeCollection;
  let viewPath = '';
  const path = modelPath.split('.');

  for (let i = 0; i < path.length - 1; i++) {
    const associationName = path[i];

    const association = collection.Associations[associationName];

    if (association) {
      if (!association.Integrated) {
        viewPath += association.AssociationName + '.';
      }

      collection = getCollectionInfo(
        association.AssociatedCollectionName,
        null,
        collectionMap
      ).collection;
    } else {
      throw new Error(
        `Could not find association ${associationName} in ${collection?.CollectionName}`
      );
    }
  }

  const attribute = collection.Attributes[path[path.length - 1]];

  if (attribute) {
    return {
      attribute: attribute,
      viewPath: viewPath + attribute.AttributeName,
      collectionRootType: collection.RootType,
      collection,
    };
  } else {
    throw new Error(
      `Could not find attribute ${path[path.length - 1]} in ${
        collection?.CollectionName
      }`
    );
  }
};

export const safeGetAttributeInfo = (
  maybeCollection: string | IClientBusinessModel,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): Result<IAttributeInfo, string> => {
  try {
    return Result.ok(
      getAttributeInfo(maybeCollection, modelPath, collectionMap)
    );
  } catch (error) {
    return Result.err(
      error instanceof Error ? error.message : 'error without message'
    );
  }
};

/**
 *
 * @param maybeCollection Could either be the collection or the collection name
 * @param modelPath
 */
export const getAttributeModel = (
  maybeCollection: string | IClientBusinessModel,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel => {
  return getAttributeInfo(maybeCollection, modelPath, collectionMap).attribute;
};

export const safeGetAttributeModel = (
  maybeCollection: string | IClientBusinessModel,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): IClientAttributeModel | undefined => {
  try {
    return getAttributeInfo(maybeCollection, modelPath, collectionMap)
      .attribute;
  } catch (error) {
    return undefined;
  }
};

export interface IAssociationInfo {
  association: IClientAssociationModel;
  viewPath: string;
}

/**
 * Returns an association from a collection with the path modelpath, also returns the
 * path it should have in the userinterface (viewPath).
 * viewPath takes into acount integrated associations
 * @param collectionName
 * @param modelPath dot delimited path to the association
 */
export const getAssociationInfo = (
  collectionName: string,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): IAssociationInfo => {
  let collection = getCollectionInfo(
    collectionName,
    null,
    collectionMap
  ).collection;
  let viewPath = '';
  const path = modelPath.split('.');

  for (let i = 0; i < path.length; i++) {
    const associationName = path[i];

    const association = collection.Associations[associationName];
    if (association) {
      if (!association.Integrated) {
        viewPath += association.AssociationName + '.';
      }

      if (i === path.length - 1) {
        return {
          association,
          viewPath,
        };
      }

      collection = getCollectionInfo(
        association.AssociatedCollectionName,
        null,
        collectionMap
      ).collection;
    } else {
      throw new Error(`Could not find association ${associationName}`);
    }
  }

  throw new Error(
    `Could not find association ${modelPath} in collection ${collectionName}`
  );
};

/**
 * Returns an association from a collection with the path modelpath, also returns the
 * path it should have in the userinterface (viewPath).
 * viewPath takes into acount integrated associations
 * Returns undefined when an error has occured
 * @param collectionName
 * @param modelPath dot delimited path to the association
 * @param collectionMap
 */
export const safeGetAssociationInfo = (
  maybeCollectionName: string,
  modelPath: string,
  collectionMap: AvenyCollectionMap
): IAssociationInfo | undefined => {
  try {
    return getAssociationInfo(maybeCollectionName, modelPath, collectionMap);
  } catch (error) {
    return undefined;
  }
};

/**
 *
 */
export const subsystemCodeFromCollectionName = (
  collectionName: string
): string => {
  collectionName = collectionName.toLowerCase();

  if (collectionName.startsWith('sys')) {
    return collectionName.substring(0, 3);
  }

  return collectionName.substring(0, 2);
};

export const findCollection = (
  collectionMap: AvenyCollectionMap,
  collectionName: string
): IClientBusinessModel | undefined =>
  collectionMap.get(collectionName.toLowerCase());

export const findCollectionResult = (
  collectionMap: AvenyCollectionMap,
  collectionName: string
): Result<IClientBusinessModel, string> => {
  const result = collectionMap.get(collectionName.toLowerCase());
  if (result) {
    return Result.ok(result);
  }
  return Result.err(`${collectionName} saknas i modellen`);
};

export const findAssociations = (
  collectionName: string,
  associationNames: string[],
  collectionMap: AvenyCollectionMap
): IClientAssociationModel[] => {
  const associations: IClientAssociationModel[] = [];
  const collection = findCollection(collectionMap, collectionName);

  if (collection) {
    let currentCollection: IClientBusinessModel = collection;
    for (let i = 0; i < associationNames.length; i++) {
      const associationName = associationNames[i];
      const association = currentCollection.Associations[associationName];

      if (association) {
        associations.push(association);

        const nextCollection = findCollection(
          collectionMap,
          association.AssociatedCollectionName
        );

        if (nextCollection) {
          currentCollection = nextCollection;
        } else {
          throw namedError(
            'MissingAssociatedCollection',
            `Could not find collection with name: ${association.AssociatedCollectionName}`
          );
        }
      } else {
        throw namedError(
          'MissingAssociation',
          `Could not find association with name: ${associationName}`
        );
      }
    }
  } else {
    throw namedError(
      'MissingCollection',
      `Could not find collection with name: ${collectionName}`
    );
  }

  return associations;
};

/** Attribute properties created and used on frontend */
export const ClientAttributeProperty = {
  groupSortOrder: 'GroupSortOrder',
  attributeSortOrder: 'AttributeSortOrder',
};

export const getGroupSortOrder = (
  attributeProperties: ClientPropertiesType | undefined
): number | undefined => {
  const groupSortOrderString = attributeProperties?.get(
    ClientAttributeProperty.groupSortOrder
  );

  const groupSortOrder =
    groupSortOrderString != null
      ? Number.parseInt(groupSortOrderString, 10)
      : undefined;

  return groupSortOrder;
};

export const getAttributeSortOrder = (
  attributeProperties?: ClientPropertiesType
): number | undefined => {
  const attributeSortOrderString = attributeProperties?.get(
    ClientAttributeProperty.attributeSortOrder
  );

  const attributeSortOrder =
    attributeSortOrderString != null
      ? Number.parseInt(attributeSortOrderString, 10)
      : undefined;

  return attributeSortOrder;
};
/** Returns an object structure for a tree of associations ending with an attribute.
 * Association names are appended with the suffix of '_Values' to fit Apollo update methods.
 * @param path dotnotated association tree ending in an attribute, ex: ADRESS or OrgPers.ORG_NAMN
 * @param value added to the ending attribute as it's value
 */
export const getAssociationUpdateValueObject = (
  path: string,
  value: any,
  data: INodeData
) => {
  const pathArray = path.split('.');
  const associationPath = init(pathArray);
  const fieldName: string | undefined = last(pathArray);
  const valueObject = fieldName
    ? {
        [fieldName]: value,
        ...getAssociationIdVersion(
          associationPath,
          associationPath.length - 1,
          data
        ),
      }
    : {};
  const pathObject = associationPath.reduceRight(
    (prev, curr, index) => ({
      _Associations: {
        [`${curr}_Values`]: prev,
      },
      ...getAssociationIdVersion(
        associationPath,
        associationPath.length - index - 2,
        data
      ),
    }),
    valueObject
  );
  return pathObject;
};

export const getAssociationIdVersion = (
  dataPath: string[],
  index: number,
  data: INodeData
) => {
  const subPath = dataPath.slice(0, index + 1);
  const pathString = subPath.join('.');
  const id = getId(data, pathString);
  const version = getVersion(data, pathString);
  return { id, _version: version };
};

export const getAttributeHeaderValue = <T extends ValueType = string>(
  data: INodeData,
  pathString: string,
  headerDefs: string[]
): T => {
  return headerDefs
    .reduce<string[]>((acc, x) => {
      const dataPath = attributeDefToDataPath([pathString, x].join('.'));
      const headerValue = path<ValueType>(dataPath, data) as string;
      if (headerValue) {
        acc.push(headerValue);
      }
      return acc;
    }, [])
    .join(' - ') as T;
};
export const getAttributeHeaderValues = (
  data: INodeData,
  pathString: string,
  headerDefs: string[]
): any => {
  return headerDefs.reduce<string[]>((acc, x) => {
    const dataPath = attributeDefToDataPath([pathString, x].join('.'));
    const headerValue = path<ValueType>(dataPath, data) as string;
    if (headerValue) {
      acc.push(headerValue);
    }
    return acc;
  }, []);
};

export const getHeaderValue = <T extends ValueType = string>(
  data: INodeData
): T => {
  return getValue(data, ['IDString']) as T;
};

export const removeEmptyObjects = (
  value: any,
  deleteEmptyProperties?: boolean
): any => {
  Object.keys(value).forEach(v => {
    if (Array.isArray(value[v])) {
      value[v] = value[v].filter((x: any) => Object.keys(x).length > 0);
      if (value[v].length === 0) {
        delete value[v];
      } else {
        value[v].forEach((x: any) => removeEmptyObjects(x));
      }
    }
    if (value[v] && typeof value[v] === 'object') {
      removeEmptyObjects(value[v]); // <-- Make a recursive call on the nested object
      if (Object.keys(value[v]).length === 0) {
        delete value[v]; // The object had no properties, so delete that property
      }
    }
    if (value[v] == null) {
      delete value[v];
    }
  });
};
